export const gameEvents = new EventTarget();

export const gameEventNames = {
  restart: 'game:restart',
  hint: 'game:hint',
  state: 'game:state',
  setLevel: 'game:set-level',
  levelClear: 'game:level-clear',
  gameOver: 'game:game-over',
  continue: 'game:continue',
  hintGranted: 'game:hint-granted',
  attemptMetrics: 'game:attempt-metrics',
  timePause: 'game:time-pause',
  timeResume: 'game:time-resume',
  timeTick: 'game:time-tick',
} as const;

export interface SetLevelDetail {
  readonly levelNumber: number;
}

export interface LevelClearDetail {
  readonly levelNumber: number;
  readonly arrowTotal: number;
  readonly elapsedMs: number;
  readonly reachedGameOver: boolean;
}

export interface GameOverDetail {
  readonly levelNumber: number;
  readonly remaining: number;
  readonly total: number;
  readonly failCount: number;
  readonly elapsedMs: number;
}

export interface ContinueDetail {
  readonly levelNumber: number;
}

export interface HintGrantedDetail {
  readonly levelNumber: number;
}

export interface AttemptMetricsDetail {
  readonly levelNumber: number;
  readonly failCount: number;
  readonly reachedGameOver: boolean;
  readonly continuedCount: number;
  readonly hintUsedCount: number;
}

export interface GameStateDetail {
  readonly remaining: number;
  readonly total: number;
  readonly failCount: number;
  readonly maxFailures: number;
  readonly hintAvailable: boolean;
  readonly message: string;
  readonly tone: 'neutral' | 'success' | 'warning';
  readonly complete: boolean;
}

export function emitGameState(detail: GameStateDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<GameStateDetail>(gameEventNames.state, { detail }));
}

export function emitSetLevel(levelNumber: number): void {
  gameEvents.dispatchEvent(new CustomEvent<SetLevelDetail>(gameEventNames.setLevel, {
    detail: { levelNumber },
  }));
}

export function emitLevelClear(detail: LevelClearDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<LevelClearDetail>(gameEventNames.levelClear, { detail }));
}

export function emitGameOver(detail: GameOverDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<GameOverDetail>(gameEventNames.gameOver, { detail }));
}

export function emitContinue(detail: ContinueDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<ContinueDetail>(gameEventNames.continue, { detail }));
}

export function emitHintGranted(detail: HintGrantedDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<HintGrantedDetail>(gameEventNames.hintGranted, { detail }));
}

export function emitAttemptMetrics(detail: AttemptMetricsDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<AttemptMetricsDetail>(gameEventNames.attemptMetrics, {
    detail,
  }));
}

export function emitTimePause(): void {
  gameEvents.dispatchEvent(new Event(gameEventNames.timePause));
}

export function emitTimeResume(): void {
  gameEvents.dispatchEvent(new Event(gameEventNames.timeResume));
}

export function emitRestart(): void {
  gameEvents.dispatchEvent(new Event(gameEventNames.restart));
}

export interface TimeTickDetail {
  readonly elapsedMs: number;
}

export interface GameEventMap {
  'game:state': GameStateDetail;
  'game:set-level': SetLevelDetail;
  'game:level-clear': LevelClearDetail;
  'game:game-over': GameOverDetail;
  'game:continue': ContinueDetail;
  'game:hint-granted': HintGrantedDetail;
  'game:attempt-metrics': AttemptMetricsDetail;
  'game:time-tick': TimeTickDetail;
  'game:restart': void;
  'game:hint': void;
  'game:time-pause': void;
  'game:time-resume': void;
}

export function onGameEvent<K extends keyof GameEventMap>(
  name: K,
  handler: (detail: GameEventMap[K]) => void,
): () => void {
  const listener = (event: Event): void => {
    if (event instanceof CustomEvent) {
      handler(event.detail);
      return;
    }
    (handler as () => void)();
  };
  gameEvents.addEventListener(name, listener);
  return () => gameEvents.removeEventListener(name, listener);
}

export function emitTimeTick(detail: TimeTickDetail): void {
  gameEvents.dispatchEvent(new CustomEvent<TimeTickDetail>(gameEventNames.timeTick, { detail }));
}
